import { AxiosHeaders, AxiosResponse } from 'axios';
import { ConfiguracaoPedidoBalcaoTO } from 'layout/topbar/topBar.model';
import { IAbstractModel } from 'model/abstract.model';
import LoginService from 'pages/login/login.service';
import { EntradaSaida, SimNao } from 'utils/types';
import { axiosRequest } from '../../axios/axios.config';
import {
    ConfiguracaoDescontoDeleteRequestDTO,
    ConfiguracaoDescontoRequestDTO,
    ConfiguracaoDescontoTO,
    TipoDescontoAcrescimoAutorizador,
    UsuarioTO,
    rowGrid,
} from './Configuracoes.model';
import { TAcenumDTO } from './ConfigurarMovimentacaoFiscal';
import { FlowConfiguracaoPoliticaVendaDTO } from './politica-venda/politica-venda.model';

const ConfiguracaoDescontoAcrescimoService = () => {
    const path = '/configuracao-desconto-acrescimo';
    const loginService = LoginService();

    return {
        getUsuarios(): Promise<AxiosResponse<Array<UsuarioTO>>> {
            return axiosRequest.get(`usuario/usuarios-do-estabelecimento`, {
                headers: loginService.getHeaders(),
            });
        },

        getConfiguracoes(): Promise<AxiosResponse<Array<rowGrid>>> {
            return axiosRequest.get(`${path}`, { headers: loginService.getHeaders() });
        },

        saveAllConfiguracoes(
            listaConfiguracoesDescontoTO: Array<rowGrid>,
            replicaOutrosEstabelecimentos: boolean,
        ): Promise<AxiosResponse<Array<rowGrid>>> {
            const configuracaoDescontoRequestDTO: ConfiguracaoDescontoRequestDTO = {
                configuracoesDescontoTO: listaConfiguracoesDescontoTO,
                replicaOutrosEstabelecimentos,
            };
            return axiosRequest.post(`${path}/all`, configuracaoDescontoRequestDTO, {
                headers: loginService.getHeaders(),
            });
        },

        updateConfiguracao(configuracaoDescontoTO: rowGrid): Promise<AxiosResponse<rowGrid>> {
            return axiosRequest.put(`${path}`, configuracaoDescontoTO, {
                headers: loginService.getHeaders(),
            });
        },

        deleteConfiguracao(
            uuid: string,
            replicaOutrosEstabelecimentos: boolean,
        ): Promise<AxiosResponse<rowGrid>> {
            const configuracaoDescontoDeleteRequestDTO: ConfiguracaoDescontoDeleteRequestDTO = {
                replicaOutrosEstabelecimentos,
                uuid,
            };
            return axiosRequest.put(`${path}/delete`, configuracaoDescontoDeleteRequestDTO, {
                headers: loginService.getHeaders(),
            });
        },

        findByUserName(): Promise<AxiosResponse<ConfiguracaoDescontoTO>> {
            return axiosRequest.get(`${path}/nome`, { headers: loginService.getHeaders() });
        },

        validaDescontoSolicitado(
            descontoSolicitado: number,
            descontoMaximo: TipoDescontoAcrescimoAutorizador,
        ): Promise<AxiosResponse<boolean>> {
            return axiosRequest.get(
                `${path}/validatedesconto/${descontoSolicitado}/${descontoMaximo}`,
                { headers: loginService.getHeaders() },
            );
        },

        getConfiguracaoDescontoAutorizador(
            tokenAutorizador: string,
            estabelecimentoUUID: string,
        ): Promise<AxiosResponse<ConfiguracaoDescontoTO>> {
            const headers = new AxiosHeaders();
            headers.set('access_token', tokenAutorizador);
            headers.set('es', estabelecimentoUUID);
            return axiosRequest.get(`${path}/nome`, { headers: headers });
        },

        getCobrancas(): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get(`flow/cobrancas`, { headers: loginService.getHeaders() });
        },

        updateCobranca(cobrancaTO: IAbstractModel): Promise<AxiosResponse<IAbstractModel>> {
            return axiosRequest.put(`configuracoes/pedidobalcao/updatecobranca`, cobrancaTO, {
                headers: loginService.getHeaders(),
            });
        },

        getCobrancaPadrao(): Promise<AxiosResponse<IAbstractModel>> {
            return axiosRequest.get(`configuracoes/pedidobalcao/cobrancapadrao`, {
                headers: loginService.getHeaders(),
            });
        },

        getConfiguracaoPoliticaVenda(): Promise<AxiosResponse<FlowConfiguracaoPoliticaVendaDTO>> {
            return axiosRequest.get(`configuracoes/politicavenda`, {
                headers: loginService.getHeaders(),
            });
        },

        updateConfiguracaoPoliticaVenda(
            configuracaoPoliticaVenda: FlowConfiguracaoPoliticaVendaDTO,
        ): Promise<AxiosResponse<FlowConfiguracaoPoliticaVendaDTO>> {
            return axiosRequest.put(`configuracoes/politicavenda`, configuracaoPoliticaVenda, {
                headers: loginService.getHeaders(),
            });
        },

        getTabelasPrecoOptions(nome: string): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get(`tabelapreco/lista-por-estabelecimento`, {
                headers: loginService.getHeaders(),
                params: { nome },
            });
        },

        getDepositosOptions(): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get('deposito/lista-por-estabelecimento', {
                headers: loginService.getHeaders(),
            });
        },

        getModelosDocumentosFiscaisOptions(): Promise<AxiosResponse<Array<TAcenumDTO>>> {
            return axiosRequest.get('configuracoes/pedidobalcao/acenum/ModeloDocumentoFiscal', {
                headers: loginService.getHeaders(),
            });
        },

        getSerieFiscalOptionByModeloFiscal(
            modeloFiscal: string,
        ): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get('seriefiscal/lista-por-estabelecimento', {
                headers: loginService.getHeaders(),
                params: { modeloFiscal },
            });
        },

        getTmfOptions(
            entradaSaida: EntradaSaida,
            nome: string,
        ): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get('tipomovimentacaofiscal', {
                headers: loginService.getHeaders(),
                params: { entradaSaida, nome },
            });
        },

        getTmfBrindeOptions(): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get('tipomovimentacaofiscal/brinde', {
                headers: loginService.getHeaders(),
            });
        },

        getCaixasObrigaVendedor(simNao: SimNao): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get('contafinanceira/caixas-estabelecimento', {
                headers: loginService.getHeaders(),
                params: { permitePDV: simNao },
            });
        },

        updateConfiguracaoPedidoBalcao(
            config: ConfiguracaoPedidoBalcaoTO,
        ): Promise<AxiosResponse<Array<ConfiguracaoPedidoBalcaoTO>>> {
            return axiosRequest.put('configuracoes/pedidobalcao', config, {
                headers: loginService.getHeaders(),
            });
        },
    };
};

export default ConfiguracaoDescontoAcrescimoService;
